import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  CalendarClock,
  FileText,
  Loader2,
  Lock,
  Plus,
  ShoppingCart,
} from 'lucide-react';
import { fetchExerciceCompletion, refermerExercice } from '../services/api';
import { euros } from '../utils/format';

const jourCourt = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

/**
 * L'atelier d'un exercice rouvert : tout ce qu'il faut pour le compléter.
 *
 * Compléter une année close se faisait d'un écran à l'autre — une facture
 * ici, un achat là — sans jamais voir l'ensemble ni savoir combien de jours
 * restaient. Cette page rassemble le travail : l'échéance en tête, les
 * chiffres qui bougent au fil de la saisie, les pièces déjà entrées, et les
 * deux gestes qui restent à faire.
 *
 * Elle n'existe que le temps de la fenêtre. Une fois l'exercice refermé, on
 * repart aux Archives — qui n'ont pas d'échéance à afficher, et n'ont donc
 * rien à faire d'un compte à rebours.
 */
export default function ExerciceCompletionPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['exercice-completion', id],
    queryFn: () => fetchExerciceCompletion(Number(id)),
    enabled: !!id,
    retry: false,
  });

  const refermer = useMutation({
    mutationFn: () => refermerExercice(Number(id)),
    onSuccess: () => {
      ['bureau-exercice-archives', 'bureau-exercice-chiffres', 'bilan'].forEach((cle) =>
        queryClient.invalidateQueries({ queryKey: [cle] })
      );
      navigate('/bureau/archives');
    },
  });

  if (isPending) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  // Un exercice refermé entre-temps — par un collègue, ou par l'échéance —
  // n'est pas une erreur : c'est une page qui n'a plus d'objet.
  if (isError) {
    return (
      <div className="p-4 lg:p-0 pb-24 lg:pb-0 space-y-4">
        <div className="card bg-amber-50 border-amber-200 text-amber-900 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold">Cet exercice n'est plus ouvert pour complétion</p>
            <p className="text-sm mt-1">{(error as Error).message}</p>
          </div>
        </div>
        <Link to="/bureau/archives" className="btn-outline inline-flex items-center gap-2 text-sm">
          <ArrowLeft className="w-4 h-4" /> Retour aux archives
        </Link>
      </div>
    );
  }

  const { annee, jusquA, joursRestants, chiffres, factures, achats } = data;
  // Trois jours ou moins : l'échéance cesse d'être une information pour
  // devenir un avertissement.
  const presse = joursRestants <= 3;

  return (
    <div className="p-4 lg:p-0 space-y-5 pb-24 lg:pb-0">
      <Link
        to="/bureau/archives"
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary-700 dark:text-gray-400"
      >
        <ArrowLeft className="w-4 h-4" /> Archives
      </Link>

      <header className="space-y-2">
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">
          Exercice de l'année {annee} — ouvert pour complétion
        </h1>
        <p
          className={`inline-flex items-center gap-2 rounded-xl px-3 py-1.5 text-sm ${
            presse
              ? 'bg-amber-50 border border-amber-200 text-amber-900 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200'
              : 'bg-primary-50 border border-primary-200 text-primary-900 dark:bg-primary-900/20 dark:border-primary-800 dark:text-primary-200'
          }`}
        >
          <CalendarClock className="w-4 h-4 shrink-0" />
          {joursRestants > 0 ? (
            <>
              Il vous reste <strong>{joursRestants} jour{joursRestants > 1 ? 's' : ''}</strong> de
              saisie{jusquA ? `, jusqu'au ${jourCourt.format(new Date(jusquA))}` : ''} — passé ce
              délai, l'exercice se referme tout seul.
            </>
          ) : (
            <>L'échéance est atteinte : l'exercice se refermera cette nuit.</>
          )}
        </p>
      </header>

      {/* Les chiffres de l'année, tels qu'ils sont maintenant : ils bougent à
          chaque pièce saisie, et c'est ce mouvement qu'on vient vérifier. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="card py-4 text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">Encaissé {annee}</p>
          <p className="text-2xl font-bold text-green-600 tabular-nums">
            {euros.format(chiffres.encaisse)}
          </p>
        </div>
        <div className="card py-4 text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">Souscrit {annee}</p>
          <p className="text-2xl font-bold text-gray-800 dark:text-gray-200 tabular-nums">
            {euros.format(chiffres.souscrit)}
          </p>
        </div>
        <div className="card py-4 text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">Dépensé {annee}</p>
          <p className="text-2xl font-bold text-gray-800 dark:text-gray-200 tabular-nums">
            {euros.format(chiffres.achats)}
          </p>
        </div>
      </div>

      {/* Les deux gestes, et la règle de chacun : la vente se rattache par un
          choix, l'achat par sa date. */}
      <div className="grid sm:grid-cols-2 gap-3">
        <Link to="/bureau/facturation" className="card hover:border-primary-300 border-2 border-transparent transition-colors">
          <p className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <FileText className="w-4 h-4 text-primary-600" /> Ajouter une facture de vente
          </p>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Sur le formulaire, choisissez <strong>« Exercice {annee} — rouvert pour
            correction »</strong>. Pour que l'encaissement compte aussi dans {annee}, datez le
            règlement dans cette année-là en marquant la facture payée.
          </p>
        </Link>

        <Link to="/bureau/achats" className="card hover:border-primary-300 border-2 border-transparent transition-colors">
          <p className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <ShoppingCart className="w-4 h-4 text-primary-600" /> Ajouter un achat
          </p>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Rien à choisir : c'est la <strong>date d'achat</strong> qui décide. Un achat daté
            en {annee} entre dans le bilan de {annee}.
          </p>
        </Link>
      </div>

      {/* Ce qui est déjà dedans : revoir la liste évite de saisir deux fois la
          même facture retrouvée — c'est l'erreur naturelle d'une reprise. */}
      <section className="card space-y-3">
        <h2 className="font-semibold text-gray-900 dark:text-gray-100">
          Pièces de vente rattachées à {annee}
          <span className="ml-2 text-sm font-normal text-gray-500">{factures.length}</span>
        </h2>
        {factures.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Aucune pour l'instant. Les factures rattachées à cet exercice apparaîtront ici.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-800 text-sm">
            {factures.map((f) => (
              <li key={f.id} className="py-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="font-mono text-xs text-gray-500">{f.numero}</span>
                <span className="text-gray-900 dark:text-gray-100">{f.sponsor ?? '—'}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  f.statut === 'payee'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
                }`}>
                  {f.statut === 'payee' ? `payée le ${f.payeeLe ? jourCourt.format(new Date(f.payeeLe)) : '—'}` : 'en attente'}
                </span>
                <span className="ml-auto tabular-nums font-medium">{euros.format(f.montant)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="card space-y-3">
        <h2 className="font-semibold text-gray-900 dark:text-gray-100">
          Achats datés en {annee}
          <span className="ml-2 text-sm font-normal text-gray-500">{achats.length}</span>
        </h2>
        {achats.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Aucun achat daté dans cette année.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-800 text-sm">
            {achats.slice(0, 30).map((a) => (
              <li key={a.id} className="py-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="text-xs text-gray-500 tabular-nums">
                  {a.dateAchat ? jourCourt.format(new Date(a.dateAchat)) : '—'}
                </span>
                <span className="text-gray-900 dark:text-gray-100">{a.libelle}</span>
                {a.fournisseur && <span className="text-xs text-gray-500">{a.fournisseur}</span>}
                <span className="ml-auto tabular-nums font-medium">{euros.format(a.montant)}</span>
              </li>
            ))}
            {achats.length > 30 && (
              <li className="py-2 text-xs text-gray-400">
                … et {achats.length - 30} autre(s) — la liste complète est dans Suivi achats.
              </li>
            )}
          </ul>
        )}
      </section>

      {/* Refermer : le geste qui clôt le travail. La fenêtre se refermerait
          seule à l'échéance — ce bouton dit simplement « j'ai fini ». */}
      <div className="card flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-semibold text-gray-900 dark:text-gray-100">C'est terminé ?</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            L'exercice {annee} retourne aux archives avec vos corrections dedans. Sans cela, il se
            refermera seul à l'échéance.
          </p>
        </div>
        <button
          type="button"
          onClick={() => refermer.mutate()}
          disabled={refermer.isPending}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium disabled:opacity-50"
        >
          {refermer.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
          Refermer l'exercice {annee}
        </button>
      </div>

      {refermer.isError && (
        <p className="text-sm text-red-600">{(refermer.error as Error).message}</p>
      )}

      {/* Un rappel discret : la porte se rouvre, mais elle repasse par une
          demande. Le savoir avant de refermer évite le regret. */}
      <p className="text-xs text-gray-400 flex items-start gap-1.5">
        <Plus className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        Une fois refermé, rouvrir {annee} demandera un nouveau déverrouillage de l'éditeur.
      </p>
    </div>
  );
}
